import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Phone, PhoneOff, Volume2, VolumeX, RefreshCw, Clock, User, ArrowUpRight, ArrowDownLeft } from 'lucide-react';
import { monitoringAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const LiveCallsPage = () => {
  const { token } = useAuth();
  const [activeCalls, setActiveCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listeningTo, setListeningTo] = useState(null);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [transcripts, setTranscripts] = useState([]);
  const wsRef = useRef(null);
  const audioContextRef = useRef(null);
  const transcriptEndRef = useRef(null);

  // Fetch active calls
  const fetchActiveCalls = useCallback(async () => {
    try {
      const response = await monitoringAPI.getActiveCalls();
      if (response.data.success) {
        setActiveCalls(response.data.data.calls || []);
      }
    } catch (error) {
      console.error('Failed to fetch active calls:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-refresh active calls every 5 seconds
  useEffect(() => {
    fetchActiveCalls();
    const interval = setInterval(fetchActiveCalls, 5000);
    return () => clearInterval(interval);
  }, [fetchActiveCalls]);

  // Scroll to bottom of transcripts
  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [transcripts]);

  // Connect to supervisor WebSocket
  const connectWebSocket = useCallback((sessionId) => {
    if (wsRef.current) {
      wsRef.current.close();
    }

    // Get the backend WebSocket URL - must use backend host, not frontend
    const getBackendWsUrl = () => {
      // Check for environment variable first
      const apiUrl = import.meta.env.VITE_API_BASE_URL;
      if (apiUrl) {
        const url = new URL(apiUrl);
        return `wss://${url.host}`;
      }
      // Fallback based on frontend hostname
      if (window.location.hostname.includes('staging-alfred-voice')) {
        return 'wss://callcenter-staging-9528.up.railway.app';
      }
      if (window.location.hostname.includes('netlify')) {
        return 'wss://aivoice-health-production.up.railway.app';
      }
      // Local development
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      return `${wsProtocol}//${window.location.host}`;
    };

    const wsBaseUrl = getBackendWsUrl();
    const wsUrl = `${wsBaseUrl}/ws/supervisor?token=${token}&sessionId=${sessionId}`;

    console.log('Connecting to supervisor WebSocket:', wsUrl);
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      console.log('Supervisor WebSocket connected');
      toast.success('Connected to live call');
      setListeningTo(sessionId);
      setTranscripts([]);
    };

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        handleWebSocketMessage(message);
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error);
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      toast.error('Connection error');
    };

    ws.onclose = (event) => {
      console.log('WebSocket closed:', event.code, event.reason);
      if (listeningTo === sessionId) {
        setListeningTo(null);
      }
    };

    wsRef.current = ws;
  }, [token, listeningTo]);

  // Handle WebSocket messages
  const handleWebSocketMessage = (message) => {
    switch (message.type) {
      case 'connected':
        console.log('Supervisor connected:', message.connectionId);
        break;

      case 'listening':
        console.log('Now listening to:', message.sessionId);
        break;

      case 'audio':
        if (audioEnabled) {
          playAudio(message.audio, message.direction);
        }
        break;

      case 'transcript':
        setTranscripts(prev => [...prev, {
          role: message.role,
          text: message.text,
          timestamp: message.timestamp
        }]);
        break;

      case 'call_ended':
        toast.info(`Call ended: ${message.reason}`);
        setListeningTo(null);
        setTranscripts(prev => [...prev, {
          role: 'system',
          text: `Call ended: ${message.reason}`,
          timestamp: message.timestamp
        }]);
        break;

      default:
        console.log('Unknown message type:', message.type);
    }
  };

  // Play audio using Web Audio API
  const playAudio = async (base64Audio, direction) => {
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({
          sampleRate: 16000
        });
      }

      const audioContext = audioContextRef.current;
      const binaryString = atob(base64Audio);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Convert 16-bit PCM to Float32
      const samples = new Float32Array(bytes.length / 2);
      for (let i = 0; i < samples.length; i++) {
        const low = bytes[i * 2];
        const high = bytes[i * 2 + 1];
        const sample = (high << 8) | low;
        samples[i] = sample >= 0x8000 ? (sample - 0x10000) / 0x8000 : sample / 0x7FFF;
      }

      const audioBuffer = audioContext.createBuffer(1, samples.length, 16000);
      audioBuffer.copyToChannel(samples, 0);

      const source = audioContext.createBufferSource();
      source.buffer = audioBuffer;

      // Apply slight panning based on direction
      const panner = audioContext.createStereoPanner();
      panner.pan.value = direction === 'caller' ? -0.3 : 0.3;

      source.connect(panner);
      panner.connect(audioContext.destination);
      source.start();
    } catch (error) {
      console.error('Failed to play audio:', error);
    }
  };

  // Start listening to a call
  const startListening = (call) => {
    connectWebSocket(call.sessionId);
  };

  // Stop listening
  const stopListening = () => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setListeningTo(null);
    setTranscripts([]);
    toast.info('Stopped listening');
  };

  // End a call
  const endCall = async (call) => {
    if (!confirm(`Are you sure you want to end the call with ${call.contactName || call.callerNumber || call.toNumber}?`)) {
      return;
    }

    try {
      const response = await monitoringAPI.endCall(call.sessionId, 'supervisor_ended');
      if (response.data.success) {
        toast.success('Call ended successfully');
        fetchActiveCalls();
        if (listeningTo === call.sessionId) {
          stopListening();
        }
      }
    } catch (error) {
      console.error('Failed to end call:', error);
      toast.error('Failed to end call');
    }
  };

  // Format duration
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Live Calls</h1>
          <p className="text-gray-600">Monitor and manage active calls in real-time</p>
        </div>
        <button
          onClick={fetchActiveCalls}
          className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Active Calls List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Phone className="w-5 h-5 text-green-600" />
              Active Calls ({activeCalls.length})
            </h2>
          </div>

          <div className="divide-y divide-gray-100 max-h-[600px] overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center text-gray-500">
                <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                Loading...
              </div>
            ) : activeCalls.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Phone className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No active calls</p>
                <p className="text-sm mt-1">Calls will appear here when they start</p>
              </div>
            ) : (
              activeCalls.map((call) => (
                <div
                  key={call.sessionId}
                  className={`p-4 hover:bg-gray-50 ${listeningTo === call.sessionId ? 'bg-green-50 border-l-4 border-green-500' : ''}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {call.direction === 'outbound' ? (
                          <ArrowUpRight className="w-4 h-4 text-blue-500" />
                        ) : (
                          <ArrowDownLeft className="w-4 h-4 text-green-500" />
                        )}
                        <span className="font-medium text-gray-900 truncate">
                          {call.contactName || call.callerNumber || 'Unknown'}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDuration(call.durationSeconds)}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs ${
                          call.direction === 'outbound' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                        }`}>
                          {call.direction}
                        </span>
                        {call.language && (
                          <span className="text-gray-400">{call.language}</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-400 mt-1 truncate">
                        {call.toNumber}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 ml-4">
                      {listeningTo === call.sessionId ? (
                        <button
                          onClick={stopListening}
                          className="p-2 text-green-700 bg-green-100 rounded-lg hover:bg-green-200"
                          title="Stop listening"
                        >
                          <VolumeX className="w-5 h-5" />
                        </button>
                      ) : (
                        <button
                          onClick={() => startListening(call)}
                          className="p-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
                          title="Listen to call"
                        >
                          <Volume2 className="w-5 h-5" />
                        </button>
                      )}
                      <button
                        onClick={() => endCall(call)}
                        className="p-2 text-red-600 bg-red-50 rounded-lg hover:bg-red-100"
                        title="End call"
                      >
                        <PhoneOff className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Live Transcript */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <User className="w-5 h-5 text-indigo-600" />
              Live Transcript
            </h2>
            {listeningTo && (
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setAudioEnabled(!audioEnabled)}
                  className={`p-2 rounded-lg ${audioEnabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}
                  title={audioEnabled ? 'Mute audio' : 'Unmute audio'}
                >
                  {audioEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                </button>
                <span className="flex items-center gap-2 text-sm text-green-600">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                  Listening
                </span>
              </div>
            )}
          </div>

          <div className="p-4 h-[500px] overflow-y-auto bg-gray-50">
            {!listeningTo ? (
              <div className="h-full flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <Volume2 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>Select a call to listen</p>
                  <p className="text-sm mt-1">Click the speaker icon on any active call</p>
                </div>
              </div>
            ) : transcripts.length === 0 ? (
              <div className="h-full flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                  <p>Waiting for conversation...</p>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {transcripts.map((t, i) => (
                  <div
                    key={i}
                    className={`p-3 rounded-lg ${
                      t.role === 'user' || t.role === 'caller'
                        ? 'bg-blue-100 text-blue-900 mr-12'
                        : t.role === 'system'
                        ? 'bg-yellow-100 text-yellow-900 mx-6 text-center text-sm'
                        : 'bg-white text-gray-900 ml-12 border border-gray-200'
                    }`}
                  >
                    <div className="text-xs text-gray-500 mb-1">
                      {t.role === 'user' || t.role === 'caller' ? 'Caller' : t.role === 'assistant' ? 'AI' : 'System'}
                    </div>
                    <p>{t.text}</p>
                  </div>
                ))}
                <div ref={transcriptEndRef} />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-medium text-blue-900 mb-2">How to use Live Call Monitoring</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• <strong>Listen:</strong> Click the speaker icon to hear the call in real-time</li>
          <li>• <strong>End Call:</strong> Click the red phone icon to immediately terminate a call</li>
          <li>• <strong>Audio:</strong> Caller audio plays slightly left, AI audio plays slightly right</li>
          <li>• <strong>Transcripts:</strong> Live transcripts appear as the conversation progresses</li>
        </ul>
      </div>
    </div>
  );
};

export default LiveCallsPage;
